import React, { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import logo from '../pages/images/logo.png';

const HAMBURGER_ITEMS = [
    { to: '/dogs',     icon: '🐾', label: 'Perros' },
    { to: '/daycare',  icon: '🌸', label: 'Guardería' },
    { to: '/hotel',    icon: '🏢', label: 'Hotel' },
    { to: '/vet',      icon: '🩺', label: 'Veterinaria', roles: ['admin', 'receptionist', 'vet'] },
    { to: '/grooming', icon: '✂️', label: 'Estética',    roles: ['admin', 'receptionist', 'vet'] },
    { to: '/pos',       icon: '💳', label: 'Ventas',     roles: ['admin', 'receptionist', 'vet'] },
    { to: '/happy-bus',    icon: '🚐', label: 'Happy Bus',   roles: ['admin', 'receptionist', 'support', 'vet'] },
];

const BOTTOM_NAV_ITEMS = [
    { to: '/dashboard', icon: '🏠', label: 'Inicio',    exact: true },
    { to: '/dogs',      icon: '🐶', label: 'Perros',    exact: false },
    { to: null,         icon: '＋', label: 'Nuevo',     fab: true },
    { to: '/daycare',   icon: '📅', label: 'Guardería', exact: false },
    { to: '/more',      icon: '⋯',  label: 'Más',       exact: false },
];

const FAB_ACTIONS = [
    { icon: '🛒', label: 'Nueva venta',        to: '/pos/new',  roles: ['admin', 'receptionist', 'vet'] },
    { icon: '📥', label: 'Entrada guardería',  to: '/daycare' },
    { icon: '📤', label: 'Salida guardería',   to: '/daycare' },
    { icon: '🏨', label: 'Reserva hotel',      to: '/hotel', state: { openTab: 'booking-form' } },
    { icon: '🩺', label: 'Cita veterinaria',   to: '/vet',      roles: ['admin', 'receptionist', 'vet'] },
    { icon: '✂️', label: 'Cita estética',      to: '/grooming', state: { openTab: 'booking-form' }, roles: ['admin', 'receptionist', 'vet'] },
    { icon: '🐶', label: 'Agregar perro',       to: '/dogs' },
    { icon: '🚐', label: 'Happy Bus',            to: '/happy-bus', roles: ['admin', 'receptionist', 'support', 'vet'] },
    { icon: '📝', label: 'Nota rápida',        to: '/dashboard' },
];

const Navigation = () => {
    const { role, logout, user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [fabOpen, setFabOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const [visibleCount, setVisibleCount] = useState(7);
    const navRef = useRef(null);
    const linksRef = useRef(null);
    const moreRef = useRef(null);

    const toggleMobileMenu = () => setIsMobileMenuOpen(prev => !prev);
    const closeMobileMenu = () => { setIsMobileMenuOpen(false); setMoreOpen(false); };

    const hamburgerItems = [
        ...HAMBURGER_ITEMS.filter(item => !item.roles || item.roles.includes(role)),
        ...(role === 'admin'
            ? [{ to: '/settings', icon: '⚙️', label: 'Configuración' }]
            : []),
    ];

    const initials = (user?.username || role || '??').slice(0, 2).toUpperCase();

    const isActive = (item) =>
        item.exact
            ? location.pathname === item.to
            : location.pathname.startsWith(item.to);

    // ── Collapsing "Más" — only active below 1128px, full nav above
    const recalcVisible = useCallback(() => {
        if (window.innerWidth < 769 || !navRef.current || !linksRef.current) {
            setVisibleCount(hamburgerItems.length);
            return;
        }
        if (window.innerWidth >= 1128) {
            setVisibleCount(hamburgerItems.length);
            return;
        }
        // Below 1128 — force collapsing so 1091 never shows 8 items in one row
        const w = window.innerWidth;
        let cap = 5; // 1091 falls here → 5 visible + 3 in Más
        if (w < 900) cap = 3;
        else if (w < 1000) cap = 4;
        // Also measure available space to avoid over-capping when very narrow
        const navW = navRef.current.clientWidth;
        const brandW = navRef.current.querySelector('.navbar-brand')?.offsetWidth || 120;
        const userW = navRef.current.querySelector('.user-area')?.offsetWidth || 90;
        const available = navW - brandW - userW - 24;
        const sample = linksRef.current.querySelector('li');
        const itemW = sample ? sample.offsetWidth + 12 : 110;
        const maxFit = Math.max(2, Math.floor((available - 90) / itemW));
        const nextVisible = Math.min(cap, maxFit, hamburgerItems.length);
        if (hamburgerItems.length * itemW <= available && w >= 1050) setVisibleCount(hamburgerItems.length);
        else setVisibleCount(Math.max(2, nextVisible));
    }, [hamburgerItems.length]);

    useLayoutEffect(() => {
        recalcVisible();
        window.addEventListener('resize', recalcVisible);
        return () => window.removeEventListener('resize', recalcVisible);
    }, [recalcVisible]);

    // Close Más when clicking outside or route changes
    useLayoutEffect(() => {
        const onClick = (e) => {
            if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
        };
        document.addEventListener('click', onClick);
        return () => document.removeEventListener('click', onClick);
    }, []);
    useLayoutEffect(() => { setMoreOpen(false); }, [location.pathname]);

    const visibleItems = hamburgerItems.slice(0, visibleCount);
    const overflowItems = hamburgerItems.slice(visibleCount);

    return (
        <>
            <nav className="navbar" ref={navRef}>
                {/* Desktop / tablet brand */}
                <Link to="/dashboard" className="navbar-brand" onClick={closeMobileMenu}>
                    <span className="navbar-paw">🐾</span>
                    <img src={logo} alt="Happy Tails" className="nav-logo" />
                </Link>

                {/* Hamburger — mobile */}
                <button
                    className={`mobile-menu-btn ${isMobileMenuOpen ? 'active' : ''}`}
                    onClick={toggleMobileMenu}
                    aria-label="Toggle mobile menu"
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>

                <ul className={`nav-links ${isMobileMenuOpen ? 'mobile-open' : ''}`} ref={linksRef}>
                    {visibleItems.map(item => (
                        <li key={item.to}>
                            <Link to={item.to} onClick={closeMobileMenu}>
                                <span className="nav-icon">{item.icon}</span>{item.label}
                            </Link>
                        </li>
                    ))}
                    {overflowItems.length > 0 && (
                        <li className="nav-more" ref={moreRef}>
                            <button
                                className={`nav-more-btn${moreOpen ? ' open' : ''}`}
                                onClick={(e) => { e.stopPropagation(); setMoreOpen(v => !v); }}
                                aria-haspopup="true"
                                aria-expanded={moreOpen}
                            >
                                Más <span className="nav-more-caret">▾</span>
                                {overflowItems.some(isActive) && <span className="nav-more-dot" />}
                            </button>
                            {moreOpen && (
                                <ul className="nav-more-dropdown">
                                    <li style={{ padding: '8px 12px 10px', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#374151', borderBottom: '1px solid #f3f4f6', marginBottom: 6 }}>
                                        <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#e0e7ff', color: '#3730a3', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800 }}>{initials}</span>
                                        <span style={{ fontSize: '0.9rem' }}>{user?.username || user?.name || role}</span>
                                        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', background: '#f3f4f6', color: '#6b7280', padding: '2px 8px', borderRadius: 20, textTransform: 'capitalize' }}>{role}</span>
                                    </li>
                                    {overflowItems.map(item => (
                                        <li key={item.to}>
                                            <Link to={item.to} onClick={closeMobileMenu}>
                                                <span className="nav-icon">{item.icon}</span>{item.label}
                                            </Link>
                                        </li>
                                    ))}
                                    <li style={{ margin: '6px 0 0', paddingTop: 6, borderTop: '1px solid #e5e7eb' }}>
                                        <button
                                            onClick={() => { setMoreOpen(false); closeMobileMenu(); logout(); }}
                                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: '#dc2626', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
                                        >
                                            <span>🚪</span> Cerrar sesión
                                        </button>
                                    </li>
                                </ul>
                            )}
                        </li>
                    )}
                    {/* Hidden measurer: render overflow items off-screen to estimate width when collapsed */}
                    <li aria-hidden style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }}>
                        {overflowItems[0] && <span>{overflowItems[0].label}</span>}
                    </li>
                </ul>

                <div className="user-area">
                    <NotificationBell />
                    {/* Avatar — taps open Settings */}
                    <button className="user-avatar" onClick={() => navigate('/settings')} title="Configuración">
                        {initials}
                    </button>
                    {overflowItems.length === 0 && (
                        <button className="logout-btn" onClick={logout}>Cerrar sesión</button>
                    )}
                </div>
            </nav>

            {/* Bottom tab bar — phones ≤600px */}
            <nav className="bottom-nav" aria-label="Main navigation">
                {BOTTOM_NAV_ITEMS.map(item => {
                    if (item.fab) {
                        return (
                            <button
                                key="fab"
                                className={`bottom-nav-item bottom-nav-item--fab${fabOpen ? ' bottom-nav-item--fab-open' : ''}`}
                                onClick={() => setFabOpen(v => !v)}
                            >
                                <span className="bottom-nav-icon">{fabOpen ? '✕' : item.icon}</span>
                                <span className="bottom-nav-label">{item.label}</span>
                            </button>
                        );
                    }
                    const active = isActive(item);
                    return (
                        <Link
                            key={item.to}
                            to={item.to}
                            className={`bottom-nav-item${active ? ' bottom-nav-item--active' : ''}`}
                            onClick={() => setFabOpen(false)}
                        >
                            <span className="bottom-nav-icon">{item.icon}</span>
                            <span className="bottom-nav-label">{item.label}</span>
                            {active && <span className="bottom-nav-dot"></span>}
                        </Link>
                    );
                })}
            </nav>
            {fabOpen && (
                <div className="fab-sheet-backdrop" onClick={() => setFabOpen(false)}>
                    <div className="fab-sheet" onClick={e => e.stopPropagation()}>
                        {FAB_ACTIONS.filter(a => !a.roles || a.roles.includes(role)).map((action, i) => (
                            <button
                                key={i}
                                className="fab-sheet-item"
                                onClick={() => { setFabOpen(false); navigate(action.to, action.state ? { state: action.state } : undefined); }}
                            >
                                <div className="fab-sheet-icon">{action.icon}</div>
                                <span className="fab-sheet-label">{action.label}</span>
                                <span className="fab-sheet-arrow">›</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default Navigation;
